import { useState, useRef } from 'react'
import { ImagePlus } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { useImageUpload } from '@/lib/hooks/use-image-upload'

interface ImageUploadTabProps {
  agentId: string
  draftId: string
  onUploaded: (imageUrl: string) => void
}

export function ImageUploadTab({ agentId, draftId, onUploaded }: ImageUploadTabProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { state, upload } = useImageUpload(agentId, draftId)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      const url = await upload(file)
      if (url) onUploaded(url)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const url = await upload(file)
      if (url) onUploaded(url)
    }
    e.target.value = ''
  }

  const isUploading = state.status === 'uploading' || state.status === 'validating'

  return (
    <div className="space-y-4 py-4">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/30 hover:border-primary'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        <ImagePlus className="size-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-foreground mb-1">Drop an image here or click to browse</p>
        <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP up to 5 MB</p>
      </div>

      {isUploading && (
        <div className="space-y-2">
          <Progress value={state.progress} />
          <p className="text-xs text-muted-foreground text-center">Uploading... {state.progress}%</p>
        </div>
      )}

      {state.status === 'error' && state.error && (
        <p className="text-sm text-destructive text-center">{state.error}</p>
      )}
    </div>
  )
}
